<template>
  <li v-if="!item.hidden" class="nav-item">
    <!-- Single Menu Item (No Children) -->
    <a 
      v-if="!item.children && !isDisabled"
      :href="item.path || '#'" 
      class="nav-link"
      :target="item.target"
      @click.prevent="handleClick"
    >
      <span v-if="item.icon" class="nav-icon">
        <i :class="item.icon"></i>
      </span>
      <span class="nav-text">{{ item.label }}</span>
      <span v-if="item.badge" :class="['badge', item.badge.class, 'badge-pill', 'text-end']">
        {{ item.badge.text }}
      </span>
    </a>
    
    <!-- Disabled Menu Item -->
    <a 
      v-else-if="!item.children && isDisabled"
      href="javascript:void(0);" 
      class="nav-link disabled"
      @click.prevent
    >
      <span v-if="item.icon" class="nav-icon">
        <i :class="item.icon"></i>
      </span>
      <span class="nav-text">{{ item.label }}</span>
      <span v-if="item.badge" :class="['badge', item.badge.class, 'badge-pill', 'text-end']">
        {{ item.badge.text }}
      </span>
    </a>
    
    <!-- Menu Item with Children (Collapsible) -->
    <template v-else>
      <a 
        :aria-controls="item.id" 
        :aria-expanded="isExpanded" 
        class="nav-link menu-arrow"
        :class="{ 'disabled': isDisabled }"
        :href="`#${item.id}`" 
        role="button"
        @click.prevent="handleToggle"
      >
        <span v-if="item.icon" class="nav-icon">
          <i :class="item.icon"></i>
        </span>
        <span class="nav-text">{{ item.label }}</span>
        <span v-if="item.badge" :class="['badge', item.badge.class, 'badge-pill', 'text-end']">
          {{ item.badge.text }}
        </span>
      </a>
      <div :class="['collapse', { 'show': isExpanded }]" :id="item.id">
        <ul class="nav sub-navbar-nav">
          <li 
            v-for="(child, childIndex) in item.children" 
            :key="childIndex"
            v-show="!child.hidden"
            class="sub-nav-item"
          >
            <!-- Simple Child Item -->
            <a 
              v-if="!child.children && !isChildDisabled(child)"
              :href="child.path || '#'" 
              class="sub-nav-link"
              :target="child.target"
              @click.prevent="handleChildClick(child)"
            >
              <span v-if="child.icon" class="sub-nav-icon me-2">
                <i :class="child.icon"></i>
              </span>
              {{ child.label }}
              <span v-if="child.badge" :class="['badge', child.badge.class, 'badge-pill', 'text-end']">
                {{ child.badge.text }}
              </span>
            </a>
            
            <!-- Disabled Child Item -->
            <a 
              v-else-if="!child.children && isChildDisabled(child)"
              href="javascript:void(0);" 
              class="sub-nav-link disabled"
              @click.prevent
            >
              <span v-if="child.icon" class="sub-nav-icon me-2">
                <i :class="child.icon"></i>
              </span>
              {{ child.label }}
              <span v-if="child.badge" :class="['badge', child.badge.class, 'badge-pill', 'text-end']">
                {{ child.badge.text }}
              </span>
            </a>
            
            <!-- Nested Sub-menu (Child with Children) -->
            <template v-else>
              <a 
                :aria-controls="child.id" 
                :aria-expanded="childExpanded[childIndex]" 
                class="sub-nav-link menu-arrow"
                :class="{ 'disabled': isChildDisabled(child) }"
                :href="`#${child.id}`" 
                role="button"
                @click.prevent="handleChildToggle(childIndex, child)"
              >
                <span v-if="child.icon" class="sub-nav-icon me-2">
                  <i :class="child.icon"></i>
                </span>
                <span>{{ child.label }}</span>
                <span v-if="child.badge" :class="['badge', child.badge.class, 'badge-pill', 'text-end']">
                  {{ child.badge.text }}
                </span>
              </a>
              <div :class="['collapse', { 'show': childExpanded[childIndex] }]" :id="child.id">
                <ul class="nav sub-navbar-nav">
                  <li 
                    v-for="(subChild, subIndex) in child.children" 
                    :key="subIndex"
                    v-show="!subChild.hidden"
                    class="sub-nav-item"
                  >
                    <!-- Simple Sub-Child Item -->
                    <a 
                      v-if="!subChild.children && !isSubChildDisabled(subChild)"
                      :href="subChild.path || '#'" 
                      class="sub-nav-link"
                      :target="subChild.target"
                      @click.prevent="handleSubChildClick(subChild)"
                    >
                      <span v-if="subChild.icon" class="sub-nav-icon me-2">
                        <i :class="subChild.icon"></i>
                      </span>
                      {{ subChild.label }}
                      <span v-if="subChild.badge" :class="['badge', subChild.badge.class, 'badge-pill', 'text-end']">
                        {{ subChild.badge.text }}
                      </span>
                    </a>
                    
                    <!-- Disabled Sub-Child Item -->
                    <a 
                      v-else-if="!subChild.children && isSubChildDisabled(subChild)"
                      href="javascript:void(0);" 
                      class="sub-nav-link disabled"
                      @click.prevent
                    >
                      <span v-if="subChild.icon" class="sub-nav-icon me-2">
                        <i :class="subChild.icon"></i>
                      </span>
                      {{ subChild.label }}
                      <span v-if="subChild.badge" :class="['badge', subChild.badge.class, 'badge-pill', 'text-end']">
                        {{ subChild.badge.text }}
                      </span>
                    </a>
                    
                    <!-- Deeply Nested Sub-Child (Sub-Child with Children) -->
                    <template v-else>
                      <a 
                        :aria-controls="subChild.id" 
                        :aria-expanded="subChildExpanded[subIndex]" 
                        class="sub-nav-link menu-arrow"
                        :class="{ 'disabled': isSubChildDisabled(subChild) }"
                        :href="`#${subChild.id}`" 
                        role="button"
                        @click.prevent="handleSubChildToggle(subIndex, subChild)"
                      >
                        <span v-if="subChild.icon" class="sub-nav-icon me-2">
                          <i :class="subChild.icon"></i>
                        </span>
                        <span>{{ subChild.label }}</span>
                        <span v-if="subChild.badge" :class="['badge', subChild.badge.class, 'badge-pill', 'text-end']">
                          {{ subChild.badge.text }}
                        </span>
                      </a>
                      <div :class="['collapse', { 'show': subChildExpanded[subIndex] }]" :id="subChild.id">
                        <ul class="nav sub-navbar-nav">
                          <li 
                            v-for="(deepChild, deepIndex) in subChild.children" 
                            :key="deepIndex"
                            v-show="!deepChild.hidden"
                            class="sub-nav-item"
                          >
                            <a 
                              v-if="!isDeepChildDisabled(deepChild)"
                              :href="deepChild.path || '#'" 
                              class="sub-nav-link"
                              :target="deepChild.target"
                              @click.prevent="handleDeepChildClick(deepChild)"
                            >
                              <span v-if="deepChild.icon" class="sub-nav-icon me-2">
                                <i :class="deepChild.icon"></i>
                              </span>
                              {{ deepChild.label }}
                              <span v-if="deepChild.badge" :class="['badge', deepChild.badge.class, 'badge-pill', 'text-end']">
                                {{ deepChild.badge.text }}
                              </span>
                            </a>
                            <a 
                              v-else
                              href="javascript:void(0);" 
                              class="sub-nav-link disabled"
                              @click.prevent
                            >
                              <span v-if="deepChild.icon" class="sub-nav-icon me-2">
                                <i :class="deepChild.icon"></i>
                              </span>
                              {{ deepChild.label }}
                              <span v-if="deepChild.badge" :class="['badge', deepChild.badge.class, 'badge-pill', 'text-end']">
                                {{ deepChild.badge.text }}
                              </span>
                            </a>
                          </li>
                        </ul>
                      </div>
                    </template>
                  </li>
                </ul>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </template>
  </li>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'MenuItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    sectionDisabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isExpanded = ref(props.item.expanded || false)
    const childExpanded = ref({})
    const subChildExpanded = ref({})

    // Check if item is disabled (either item itself or section is disabled)
    const isDisabled = computed(() => {
      return props.item.disabled || props.sectionDisabled
    })

    // Check if child is disabled
    const isChildDisabled = (child) => {
      return child.disabled || props.sectionDisabled
    }

    // Check if sub-child is disabled
    const isSubChildDisabled = (subChild) => {
      return subChild.disabled || props.sectionDisabled
    }

    // Check if deep child is disabled
    const isDeepChildDisabled = (deepChild) => {
      return deepChild.disabled || props.sectionDisabled
    }

    const toggleExpand = () => {
      if (!isDisabled.value) {
        isExpanded.value = !isExpanded.value
      }
    }

    const handleToggle = () => {
      if (!isDisabled.value) {
        toggleExpand()
      }
    }

    const handleClick = (e) => {
      if (isDisabled.value) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
      // Allow normal navigation if not disabled
    }

    const toggleChildExpand = (index) => {
      childExpanded.value[index] = !(childExpanded.value[index] ?? false)
    }

    const handleChildToggle = (index, child) => {
      if (!isChildDisabled(child)) {
        toggleChildExpand(index)
      }
    }

    const toggleSubChildExpand = (index) => {
      subChildExpanded.value[index] = !(subChildExpanded.value[index] ?? false)
    }

    const handleSubChildToggle = (index, subChild) => {
      if (!isSubChildDisabled(subChild)) {
        toggleSubChildExpand(index)
      }
    }

    const handleChildClick = (child) => {
      if (!isChildDisabled(child) && child.path) {
        window.location.href = child.path
      }
    }

    const handleSubChildClick = (subChild) => {
      if (!isSubChildDisabled(subChild) && subChild.path) {
        window.location.href = subChild.path
      }
    }

    const handleDeepChildClick = (deepChild) => {
      if (!isDeepChildDisabled(deepChild) && deepChild.path) {
        window.location.href = deepChild.path
      }
    }

    return {
      isExpanded,
      childExpanded,
      subChildExpanded,
      isDisabled,
      isChildDisabled,
      isSubChildDisabled,
      isDeepChildDisabled,
      toggleExpand,
      handleToggle,
      handleClick,
      toggleChildExpand,
      handleChildToggle,
      toggleSubChildExpand,
      handleSubChildToggle,
      handleChildClick,
      handleSubChildClick,
      handleDeepChildClick
    }
  }
}
</script>

<style scoped>
/* Increase font size for menu items */
.nav-link {
  font-size: 1.1rem;
}

.sub-nav-link {
  font-size: 1rem;
}
</style>
